import React, { useState, useEffect, useCallback } from 'react';
import { Button, Upload, message, Card, Row, Col, Table, Alert, Select, Modal, Form, Input, Checkbox } from 'antd';
import { UploadOutlined, PlusOutlined } from '@ant-design/icons';
import { 
  uploadBatch, confirmBatch, getBankList, createBank, 
  getBankNames, getBankCountrys, getProjectList, getFullKeyList, getBatchTransactions
} from '../../services/api';
import styles from './BatchPage.module.css';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

const BatchCreate = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [validRecords, setValidRecords] = useState([]);
  const [invalidRecords, setInvalidRecords] = useState([]);
  const [validPagination, setValidPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [invalidPagination, setInvalidPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [tableLoading, setTableLoading] = useState(false);
  
  // Bank Selection State
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);

  // Add Bank Modal State
  const [isAccountModalVisible, setIsAccountModalVisible] = useState(false);
  const [accountForm] = Form.useForm();
  const [projects, setProjects] = useState([]);
  const [bankNames, setBankNames] = useState([]);
  const [bankCountrys, setBankCountrys] = useState([]);
  const [keys, setKeys] = useState([]);
  const [answers, setAnswers] = useState([{ key: '', value: '' }]);

  const fetchBanks = useCallback(async () => {
    try {
      const response = await getBankList({ payout_system: 1 });
      setBanks(response.data);
    } catch (err) {
      message.error('Failed to load bank accounts.');
    }
  }, []);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  // Account Creation Logic
  const fetchAccountDependencies = useCallback(async () => {
    try {
      const [projRes, nameRes, countryRes, keyRes] = await Promise.all([
        getProjectList(),
        getBankNames(),
        getBankCountrys(),
        getFullKeyList()
      ]);
      setProjects(projRes.data);
      setBankNames(nameRes.data);
      setBankCountrys(countryRes.data);
      const formattedKeys = keyRes.data.map((key) => ({
        id: key.id,
        display: `${key.bank_name.toUpperCase()} | ${key.type.toUpperCase()}`,
      }));
      setKeys(formattedKeys);
    } catch (err) {
      message.error('Failed to load account creation dependencies.');
    }
  }, []);

  useEffect(() => {
    if (isAccountModalVisible) {
      fetchAccountDependencies();
    }
  }, [isAccountModalVisible, fetchAccountDependencies]);

  const handleCreateAccount = async () => {
    try {
      const values = await accountForm.validateFields();
      const payload = { ...values, answers, payout_system: 1 };
      await createBank(payload);
      message.success('Account successfully created.');
      setIsAccountModalVisible(false);
      accountForm.resetFields();
      setAnswers([{ key: '', value: '' }]);
      fetchBanks(); // Refresh bank list
    } catch (err) {
      if (err.response?.data) {
        message.error(`Error creating account: ${JSON.stringify(err.response.data)}`);
      } else {
        message.error('Error creating account.');
      }
    }
  };

  const addAnswer = () => setAnswers([...answers, { key: '', value: '' }]);
  const removeAnswer = (index) => setAnswers(answers.filter((_, i) => i !== index));
  const updateAnswer = (index, field, value) => {
    const updatedAnswers = [...answers];
    updatedAnswers[index][field] = value;
    setAnswers(updatedAnswers);
  };

  const handleBeforeUpload = (file) => {
    setFile(file);
    return false;
  };

  const handleAnalyze = async () => {
    if (!file) {
      message.error('Please select a file to upload.');
      return;
    }
    setLoading(true);
    setError(null);
    setPreview(null);
    setValidRecords([]);
    setInvalidRecords([]);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await uploadBatch(formData);
      const previewData = response.data;
      setPreview(previewData);

      if (previewData.valid_preview) {
        setValidRecords(previewData.valid_preview);
      }
      if (previewData.invalid_preview) {
        setInvalidRecords(previewData.invalid_preview);
      }

      setValidPagination(prev => ({ ...prev, total: previewData.valid_records, current: 1 }));
      setInvalidPagination(prev => ({ ...prev, total: previewData.invalid_records, current: 1 }));

      message.success('File analyzed successfully.');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to analyze file.';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleValidTableChange = async (pagination) => {
    if (!preview || !preview.batch_id) return;
    setTableLoading(true);
    try {
      const response = await getBatchTransactions(preview.batch_id, {
        status: 'PENDING',
        page: pagination.current,
        limit: pagination.pageSize,
      });
      const data = response.data;
      // Adding more fallbacks for the records array key, similar to BatchDetails.js
      const records = Array.isArray(data.transactions) ? data.transactions :
                      Array.isArray(data.transactions?.data) ? data.transactions.data :
                      Array.isArray(data.items) ? data.items :
                      Array.isArray(data.items?.data) ? data.items.data :
                      Array.isArray(data.records) ? data.records :
                      Array.isArray(data.records?.data) ? data.records.data :
                      Array.isArray(data.data) ? data.data :
                      Array.isArray(data) ? data : [];
      setValidRecords(records);
      const paginationData = data.pagination || data.transactions || {};
      setValidPagination({
        ...pagination,
        total: paginationData.total || preview.valid_records,
      });
    } catch (err) {
      message.error('Failed to fetch valid records page.');
    } finally {
      setTableLoading(false);
    }
  };

  const handleInvalidTableChange = async (pagination) => {
    if (!preview || !preview.batch_id) return;
    setTableLoading(true);
    try {
      const response = await getBatchTransactions(preview.batch_id, {
        status: 'INVALID',
        page: pagination.current,
        limit: pagination.pageSize,
      });
      const data = response.data;
      // Adding more fallbacks for the records array key
      const records = Array.isArray(data.transactions) ? data.transactions :
                      Array.isArray(data.transactions?.data) ? data.transactions.data :
                      Array.isArray(data.items) ? data.items :
                      Array.isArray(data.items?.data) ? data.items.data :
                      Array.isArray(data.records) ? data.records :
                      Array.isArray(data.records?.data) ? data.records.data :
                      Array.isArray(data.data) ? data.data :
                      Array.isArray(data) ? data : [];
      setInvalidRecords(records);
      const paginationData = data.pagination || data.transactions || {};
      setInvalidPagination({
        ...pagination,
        total: paginationData.total || preview.invalid_records,
      });
    } catch (err) {
      message.error('Failed to fetch invalid records page.');
    } finally {
      setTableLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview || !preview.batch_id) {
      message.error('No batch to confirm.');
      return;
    }
    if (!selectedBank) {
      message.error('Please select a source bank account.');
      return;
    }
    setLoading(true);
    try {
      await confirmBatch({ batch_id: preview.batch_id, source_account_id: selectedBank });
      message.success('Batch processing started.');
      navigate('/batch-payouts');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to confirm batch.';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const errorColumns = [
    { title: 'Row', dataIndex: 'row_number', key: 'row_number' },
    { title: 'Error', dataIndex: 'error', key: 'error' },
  ];

 const validColumns = [
    { title: 'Row', dataIndex: 'row_number', key: 'row_number', width: 80 },
    { title: 'Ref ID', dataIndex: 'reference_id', key: 'reference_id' },
    { title: 'Beneficiary Name', dataIndex: 'beneficiary_name', key: 'beneficiary_name' },
    { title: 'Account No', dataIndex: 'beneficiary_account', key: 'beneficiary_account' },
    { title: 'IFSC', dataIndex: 'ifsc_code', key: 'ifsc_code' },
    { title: 'Amount', dataIndex: 'amount', key: 'amount', render: (val) => `₹${val}` },
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
  ];

  return (
    <div className={styles.container}>
      <h2>Create Batch / Upload</h2>
      {error && <Alert message="Error" description={error} type="error" showIcon closable style={{ marginBottom: 24 }} />}
      
      <div style={{ marginBottom: 24, display: 'flex', gap: 16, alignItems: 'center' }}>
        <Select 
          placeholder="Select Source Bank Account" 
          style={{ width: 300 }}
          onChange={setSelectedBank}
          value={selectedBank}
        >
          {banks
            .map(bank => (
              <Option key={bank.id} value={bank.id}>
                {bank.account_name} ({bank.bank_login})
              </Option>
            ))}
        </Select>
        <Button icon={<PlusOutlined />} onClick={() => setIsAccountModalVisible(true)}>
          Add Bank
        </Button>
      </div>

      <div className={styles.uploadSection}>
        <Upload
          name="file"
          accept=".xlsx,.xls"
          beforeUpload={handleBeforeUpload}
          onRemove={() => setFile(null)}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Upload Beneficiary List (.xlsx, .xls)</Button>
        </Upload>
        <Button
          type="primary"
          onClick={handleAnalyze}
          loading={loading}
          style={{ marginTop: 16 }}
          disabled={!file}
        >
          Analyze File
        </Button>
      </div>

      {preview && (
        <div className={styles.previewSection}>
          <Row gutter={16} className={styles.summaryCards}>
            <Col span={8}>
              <Card title="Total Records">{preview.total_records ?? 'N/A'}</Card>
            </Col>
            <Col span={8}>
              <Card title="Valid Records">{preview.valid_records ?? 'N/A'}</Card>
            </Col>
            <Col span={8}>
              <Card title="Invalid Records">{preview.invalid_records ?? 'N/A'}</Card>
            </Col>
          </Row>

          {invalidRecords.length > 0 && (
            <div className={styles.errorTable}>
              <h3>Invalid Rows Preview</h3>
              <Table
                dataSource={invalidRecords}
                columns={errorColumns}
                rowKey="row_number"
                pagination={invalidPagination}
                loading={tableLoading}
                onChange={handleInvalidTableChange}
              />
            </div>
          )}

          {validRecords.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <h3>Valid Rows Preview</h3>
              <Table
                dataSource={validRecords}
                columns={validColumns}
                rowKey="row_number"
                pagination={validPagination}
                loading={tableLoading}
                onChange={handleValidTableChange}
              />
            </div>
          )}

          <Button
            type="primary"
            onClick={handleConfirm}
            loading={loading}
            style={{ marginTop: 24 }}
            disabled={!preview || preview.valid_records === 0}
          >
            Confirm & Process
          </Button>
        </div>
      )}

      {/* Add Account Modal */}
      <Modal
        title="Add Account"
        visible={isAccountModalVisible}
        onOk={handleCreateAccount}
        onCancel={() => setIsAccountModalVisible(false)}
      >
        <Form form={accountForm} layout="vertical">
          <Form.Item
            name="name"
            label="Bank Name"
            rules={[{ required: true, message: 'Select a bank name' }]}
          >
            <Select placeholder="Select a bank">
              {bankNames.map((name) => (
                <Option key={name} value={name}>
                  {name.toUpperCase()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="country"
            label="Bank Country"
            rules={[{ required: true, message: 'Select a country' }]}
          >
            <Select placeholder="Select a country">
              {bankCountrys.map((name) => (
                <Option key={name} value={name}>
                  {name.toUpperCase()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="role" label="Account Role">
            <Select placeholder="Select a role (optional)">
              <Option value="">None</Option>
              <Option value="Source">Source</Option>
              <Option value="Pool">Pool</Option>
              <Option value="CMS">CMS</Option>
            </Select>
          </Form.Item>

          <Form.Item name="account_name" label="Account Name">
            <Input placeholder="Enter account name" />
          </Form.Item>
          <Form.Item name="bank_login" label="Login" rules={[{ required: true, message: 'Login is required' }]}>
            <Input placeholder="Enter login" />
          </Form.Item>
          <Form.Item name="bank_user_id" label="User ID">
            <Input placeholder="Enter user ID" />
          </Form.Item>
          <Form.Item name="bank_password" label="Password" rules={[{ required: true, message: 'Password is required' }]}>
            <Input.Password placeholder="Enter password" />
          </Form.Item>
          <Form.Item name="phone_number" label="Phone Number">
            <Input placeholder="Enter phone number" />
          </Form.Item>
          <Form.Item name="account_number" label="Account Number">
            <Input placeholder="Enter account number" />
          </Form.Item>
          <Form.Item name="corporate" valuePropName="checked">
            <Checkbox>Corporate Account</Checkbox>
          </Form.Item>
          <Form.Item
            name="project_id"
            label="Project"
            rules={[{ required: true, message: 'Select a project' }]}
          >
            <Select placeholder="Select a project">
              {projects.map((project) => (
                <Option key={project.id} value={project.id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="current_key_id"
            label="Key"
            rules={[{ required: true, message: 'Select a key' }]}
          >
            <Select placeholder="Select a key">
              {keys.map((key) => (
                <Option key={key.id} value={key.id}>
                  {key.display}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Security Answers">
            {answers.map((answer, index) => (
              <div key={index} style={{ display: 'flex', marginBottom: '8px' }}>
                <Input
                  placeholder="Key"
                  value={answer.key}
                  onChange={(e) => updateAnswer(index, 'key', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                <Input
                  placeholder="Value"
                  value={answer.value}
                  onChange={(e) => updateAnswer(index, 'value', e.target.value)}
                  style={{ marginRight: '8px' }}
                />
                <Button danger onClick={() => removeAnswer(index)}>
                  Delete
                </Button>
              </div>
            ))}
            <Button type="dashed" onClick={addAnswer} style={{ marginTop: '8px' }}>
              Add Pair
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
export default BatchCreate;